import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from '@apollo/react-hooks';
import { ThemeProvider } from 'styled-components';
import { getLangCode } from '../../utils/language';
import assignmentCreate from '../../apollo/mutations/assignmentCreate.graphql';
import mlo from '../../apollo/queries/mlo.graphql';
import Spinner from '../common/Spinner/Spinner';
import { useErrorHandler } from '../common/ErrorHandler/ErrorHandler';
import handleGraphQLMutationErrors from '../../helpers/handleGraphQLMutationErrors';
import Assignment from './Assignment';

const languages = [
  { short: 'EN', long: 'ENGLISH' },
  { short: 'AR', long: 'ARABIC' },
];
const parseLanguage = (lang = '') => {
  const matchingLanguage = languages.find(
    language => language.short === lang.toUpperCase(),
  );
  if (matchingLanguage) {
    return matchingLanguage.long;
  }
  return languages[0].long;
};

const AssignmentCreate = ({
  classId,
  mloId,
  trimesterId,
  instructionalPlanId,
  onGoBack,
  teachingPeriodId,
}) => {
  const { t, i18n } = useTranslation();
  const { handleError } = useErrorHandler();
  const handleErrorCallback = useCallback(() => {
    handleError();
  }, []);

  const { data, loading } = useQuery(mlo, {
    variables: { classId, mloId },
    onError: handleErrorCallback,
  });

  const parsedLanguage = parseLanguage(i18n.language);
  const langCode = getLangCode(parsedLanguage);

  const [assignmentCreateMutation] = useMutation(assignmentCreate, {
    onError: error =>
      handleGraphQLMutationErrors(error, 'NetworkError', langCode),
  });

  if (loading) {
    return <Spinner isCentered={true} testId="assignmentSpinner" />;
  }

  if (!data || !data.mlo) {
    handleErrorCallback();
    return <></>;
  }

  return (
    <ThemeProvider
      theme={{
        dir: i18n.dir(),
        lang: i18n.language,
        langCode,
      }}
    >
      <Assignment
        mutation={assignmentCreateMutation}
        title={t('Add Assignment')}
        mloData={{
          ...data.mlo,
          classId,
          mloId,
          trimesterId,
          instructionalPlanId,
          teachingPeriodId,
        }}
        language={parsedLanguage}
        onGoBack={onGoBack}
      />
    </ThemeProvider>
  );
};

AssignmentCreate.defaultProps = {
  onGoBack: () => {},
  instructionalPlanId: null,
  trimesterId: null,
  teachingPeriodId: null,
};

AssignmentCreate.propTypes = {
  history: PropTypes.object.isRequired,
  classId: PropTypes.string.isRequired,
  mloId: PropTypes.string.isRequired,
  trimesterId: PropTypes.string,
  instructionalPlanId: PropTypes.string,
  onGoBack: PropTypes.func,
  teachingPeriodId: PropTypes.string,
};

export default AssignmentCreate;
export { parseLanguage };
